/**
 * DealAgeIndicator - Indicador visual de idade do deal
 * 
 * Características:
 * - Mostra idade formatada: "2d", "3sem", "2m"
 * - Cor muda baseada na idade (envelhecimento)
 * - Verde: <15d, Amarelo: 15-45d, Vermelho: >45d
 * 
 * Versão: 1.0.0
 */
import React from 'react';
import { differenceInMinutes, differenceInHours, differenceInDays, differenceInWeeks, differenceInMonths } from 'date-fns';
import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DealAgeIndicatorProps {
  /** Data de referência (criação ou atualização de fase) */
  createdAt: string;
  /** Mostrar ícone */
  showIcon?: boolean;
  /** Classes CSS adicionais */
  className?: string;
  /** v2.3.0: Label customizado para tooltip (ex: "Na fase há") */
  tooltipLabel?: string;
}

/**
 * Formata a idade do deal de forma compacta (estilo WhatsApp)
 * Ex: 3min, 2h, 3d, 2sem, 3m
 */
function formatAge(createdAt: string): { label: string; minutes: number } {
  const now = new Date();
  const created = new Date(createdAt);
  
  const minutes = differenceInMinutes(now, created);
  const hours = differenceInHours(now, created);
  const days = differenceInDays(now, created);
  const weeks = differenceInWeeks(now, created);
  const months = differenceInMonths(now, created);

  // Formato estilo WhatsApp: granularidade decrescente
  if (minutes < 60) {
    return { label: `${Math.max(1, minutes)}min`, minutes };
  }
  if (hours < 24) {
    return { label: `${hours}h`, minutes };
  }
  if (days < 7) {
    return { label: `${days}d`, minutes };
  }
  if (weeks < 4) {
    return { label: `${weeks}sem`, minutes };
  }
  return { label: `${months}m`, minutes };
}

/**
 * Retorna a cor baseada na idade em minutos (converte para dias)
 */
function getAgeColor(minutes: number): string {
  const days = Math.floor(minutes / (60 * 24));
  if (days <= 15) return 'text-emerald-600';
  if (days <= 45) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Retorna a cor de fundo baseada na idade em minutos
 */
function getAgeBgColor(minutes: number): string {
  const days = Math.floor(minutes / (60 * 24));
  if (days <= 15) return 'bg-emerald-500/10';
  if (days <= 45) return 'bg-amber-500/10';
  return 'bg-red-500/10';
}

export function DealAgeIndicator({
  createdAt,
  showIcon = false,
  className,
  tooltipLabel,
}: DealAgeIndicatorProps) {
  const { label, minutes } = formatAge(createdAt);
  const textColor = getAgeColor(minutes);
  const bgColor = getAgeBgColor(minutes);
  const days = Math.floor(minutes / (60 * 24));
  const prefix = tooltipLabel || 'Criado há';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium tabular-nums',
              textColor,
              bgColor,
              className
            )}
          >
            {showIcon && <Clock className="h-3 w-3" />}
            <span>{label}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p>{prefix} {days} {days === 1 ? 'dia' : 'dias'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
